import React, { memo, useCallback, useMemo, useEffect, useRef, useState } from "react";
import { Grid } from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import { Dialog, DialogLoadingPlaceholder } from "@shared/components";
import { InvestorRequestType } from "@investorRequest/models";
import GeneralRequest from "@investorRequest/containers/GeneralRequest";
import WireInstructionsRequest from "@investorRequest/containers/WireInstructionsRequest";
import WireInstructionsRequestLegacy from "@investorRequest/containers/WireInstructionsRequestLegacy";
import NoDataRequest from "@investorRequest/containers/NoDataRequest";
import InvestorRequestControls from "@investorRequest/containers/InvestorRequestControls";
import WireInstructionsControls from "@investorRequest/containers/WireInstructionsControls";
import InvestorRequestTypesDropdown from "@investorRequest/components/InvestorRequestTypesDropdown";
import InvestorRequestFormTitle from "@investorRequest/components/InvestorRequestFormTitle";
import WireInstructionOptionsDialog from "@investorRequest/components/WireInstructionOptionsDialog";
import WireInstructionsUploadRequest from "@investorRequest/containers/WireInstructionsUploadRequest";
import { WireInstructionBackButton } from "@investorRequest/components/WireInstructionBackButton";
import {
  closeInvestorRequestDialog,
  resetInvestorRequestState,
  setWireUnavailable,
  selectRequestType,
  resetWireFormForBack,
  setWireFormStep,
  clearSubmitSuccess,
} from "@investorRequest/store/ducks";
import { investorRequestSelector } from "@investorRequest/store/selectors";
import { submitFormThunk, checkC2DSAvaliabilityThunk } from "@investorRequest/store/thunks";
import { useAppDispatch, useAppSelector, useMobileLayout } from "@app/hooks";
import FeatureToggleService from "@services/FeatureToggleService";
import { ToggledFeature } from "@shared/models/featureToggle";

const InvestorRequestDialog = () => {
  const { open, requestType, loading, submitSuccess } = useAppSelector(investorRequestSelector);
  const dispatch = useAppDispatch();
  const isExpandedWireEnabled = FeatureToggleService.isFeatureEnabled(ToggledFeature.ExpandedWireInstructions);

  const TransitionProps = useMemo<TransitionProps>(
    () => ({
      onExited: () => dispatch(resetInvestorRequestState()),
    }),
    [dispatch]
  );

  const handleCloseDialog = useCallback(() => {
    dispatch(closeInvestorRequestDialog());
  }, [dispatch]);

  const handleSelectRequestType = useCallback(
    (requestType: InvestorRequestType) => {
      dispatch(setWireUnavailable(false));
      dispatch(selectRequestType({ requestType }));
    },
    [dispatch]
  );

  const handleSubmit = useCallback(() => dispatch(submitFormThunk()), [dispatch]);
  const isMobile = useMobileLayout();

  const isWireInstructionsRequest = requestType === InvestorRequestType.UpdateWireInstructions;
  const isExpandedWire = isExpandedWireEnabled && isWireInstructionsRequest;

  const [wireInstructionSelection, setWireInstructionSelection] = useState<"manual" | "upload" | null>(null);

  const wasOpenRef = useRef(open);

  useEffect(() => {
    const wasOpen = wasOpenRef.current;

    wasOpenRef.current = open;

    if (!isExpandedWire || !isWireInstructionsRequest) {
      setWireInstructionSelection(null);

      return;
    }

    if (open && !wasOpen) {
      setWireInstructionSelection(null);
    }
  }, [open, isExpandedWire, isWireInstructionsRequest]);

  const shouldAutoCloseAfterSubmit =
    submitSuccess &&
    !(isExpandedWire && (wireInstructionSelection === "manual" || wireInstructionSelection === "upload"));

  useEffect(() => {
    if (!shouldAutoCloseAfterSubmit) return;

    dispatch(closeInvestorRequestDialog());
    dispatch(clearSubmitSuccess());
  }, [dispatch, shouldAutoCloseAfterSubmit]);

  const shouldUseWireControls = isExpandedWire && wireInstructionSelection === "manual";
  const ControlsComponent = shouldUseWireControls ? WireInstructionsControls : InvestorRequestControls;

  const shouldShowControls = !isExpandedWire || wireInstructionSelection !== null;
  const shouldShowRequestHeader = !(
    isExpandedWire &&
    (wireInstructionSelection === "upload" || wireInstructionSelection === "manual")
  );
  const shouldRenderControlsInActions = !isMobile && !shouldUseWireControls && shouldShowControls;

  const handleBack = useCallback(() => {
    dispatch(resetWireFormForBack());
    setWireInstructionSelection(null);
  }, [dispatch]);

  const handleFillManually = useCallback(async () => {
    const result = await dispatch(checkC2DSAvaliabilityThunk());

    dispatch(setWireUnavailable(!result.payload));

    if (result.payload) {
      dispatch(setWireFormStep("fillManually"));
      setWireInstructionSelection("manual");
    } else {
      setWireInstructionSelection(null);
    }
  }, [dispatch]);

  const handleUploadDocument = useCallback(() => {
    dispatch(setWireUnavailable(false));
    dispatch(setWireFormStep("upload"));
    setWireInstructionSelection("upload");
  }, [dispatch]);

  return (
    <Dialog
      onClose={handleCloseDialog}
      open={open}
      TransitionProps={TransitionProps}
      title="New Request"
      maxWidth="lg"
      fullWidth
      actions={
        shouldRenderControlsInActions ? <ControlsComponent onSubmit={handleSubmit} loading={loading} /> : undefined
      }
    >
      <Grid container spacing={isMobile ? 7.5 : 10}>
        {shouldShowRequestHeader && (
          <>
            <InvestorRequestFormTitle text="Have a question? Let us know, we are listening." />
            <InvestorRequestTypesDropdown requestType={requestType} onSelect={handleSelectRequestType} />
          </>
        )}
        {loading && requestType ? (
          <DialogLoadingPlaceholder testId="investor-request-loading-placeholder" />
        ) : (
          <>
            {requestType === InvestorRequestType.General && <GeneralRequest />}
            {requestType === InvestorRequestType.UpdateWireInstructions &&
              (isExpandedWire ? (
                wireInstructionSelection === "manual" ? (
                  <>
                    <WireInstructionBackButton onClick={handleBack} />
                    <WireInstructionsRequest />
                  </>
                ) : wireInstructionSelection === "upload" ? (
                  <>
                    <WireInstructionBackButton onClick={handleBack} />
                    <WireInstructionsUploadRequest />
                  </>
                ) : (
                  <WireInstructionOptionsDialog
                    onFillManually={handleFillManually}
                    onUploadDocument={handleUploadDocument}
                  />
                )
              ) : (
                <WireInstructionsRequestLegacy />
              ))}
            {requestType === InvestorRequestType.NoData && <NoDataRequest />}
          </>
        )}
        {shouldShowControls && !shouldRenderControlsInActions && (
          <ControlsComponent onSubmit={handleSubmit} loading={loading} />
        )}
      </Grid>
    </Dialog>
  );
};

export default memo(InvestorRequestDialog);
